import { PageHero } from "../components/shared/Primitives";

export default function PrivacyPolicy() {
  return (
    <div data-testid="page-privacy-policy">
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        description="How Just Care Community (JCC) collects, uses, stores and protects your information."
      />

      <section className="section pt-12 md:pt-16 pb-24">
        <div className="container-jcc max-w-3xl space-y-8 text-ink-muted leading-relaxed" data-testid="privacy-policy-body">
          <p className="text-sm text-ink-soft">Last updated: September 2026</p>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">1. Who we are</h2>
            <p>
              Just Care Community ("JCC", "we", "us") operates justcarecommunity.com, a UK-focused
              resource and connection hub for independent self-employed carers and the families who
              hire them. JCC is not a care agency and does not employ, supply, vet, manage, supervise
              or guarantee carers. For any privacy question, contact us at{" "}
              <a href="mailto:hello@justcarecommunity.com" className="text-teal hover:underline">hello@justcarecommunity.com</a>.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">2. Information we collect</h2>
            <p>Depending on how you use JCC, we may collect:</p>
            <ul className="mt-2 space-y-1.5 list-disc list-inside marker:text-teal">
              <li>Account details — name, email address, and membership/entitlement status.</li>
              <li>Carer profile information — experience, availability, rates, documents such as DBS or insurance references, and an uploaded profile photo.</li>
              <li>Care Needs post details — information about the care required, submitted by a family member.</li>
              <li>An optional closure outcome — if you choose to tell us whether you found a carer through JCC when you close a Care Need.</li>
              <li>Messages and documents exchanged inside a Care Discussion Room between a specific family and carer.</li>
              <li>Care Arrangement and "Getting Ready for Care" details — rates, rotas, household, emergency contact and starting-care information you choose to record.</li>
              <li>Payment information — handled entirely by Stripe (see Section 4); JCC does not store your card details.</li>
              <li>Basic technical information needed to keep you securely signed in.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">3. How we use your information</h2>
            <p>We use your information only to:</p>
            <ul className="mt-2 space-y-1.5 list-disc list-inside marker:text-teal">
              <li>Provide the JCC service — carer profiles, Care Needs matching, Care Discussion Rooms, Care Arrangements and related tools.</li>
              <li>Process membership payments and manage your subscription.</li>
              <li>Send service emails — sign-in links, review decisions, notifications and reminders directly related to your account or an active Care Room.</li>
              <li>Review carer profiles and Care Needs posts before they are published, for safety and quality.</li>
              <li>Protect the JCC service — preventing misuse, fraud and safeguarding issues.</li>
              <li>
                Understand whether JCC is helping people connect — if you answer the optional "did
                you find a carer through JCC?" question when closing a Care Need, we use that answer
                only to see whether the platform is working as intended. We never use it to assess
                care quality, carer performance, or to monitor any ongoing care arrangement.
              </li>
            </ul>
            <p className="mt-2">
              We will never sell your personal information, and we will never use it for marketing
              unrelated to the JCC service you've signed up for.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">4. Who we share it with</h2>
            <p>We share information only with the service providers needed to run JCC, and only to the extent needed:</p>
            <ul className="mt-2 space-y-1.5 list-disc list-inside marker:text-teal">
              <li><strong>Stripe</strong> — processes membership payments. Stripe receives your payment details directly; JCC never sees or stores your full card number.</li>
              <li><strong>Resend</strong> — delivers transactional emails such as sign-in links, notifications and reminders.</li>
              <li><strong>Systeme.io</strong> — our email/CRM platform. It receives your email address and membership tags (e.g. which tier you hold) so we can manage service communications — never your profile content, Care Needs details or Care Room messages.</li>
            </ul>
            <p className="mt-2">We do not use third-party advertising or analytics trackers on this site.</p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">5. Public visibility and your controls</h2>
            <p>
              Some information is deliberately shown to other users so JCC can work as a connection
              platform. You control this:
            </p>
            <ul className="mt-2 space-y-1.5 list-disc list-inside marker:text-teal">
              <li>
                <strong>Carer profiles</strong> — once approved, your profile can appear in the Carer
                directory to families browsing JCC. You can hide your profile from the directory at
                any time. A separate, optional "Public Sharing Link" lets non-members view a reduced
                version of your profile (never your email, phone number or private account details) —
                this is off by default and you can switch it off at any time.
              </li>
              <li>
                <strong>Care Needs posts</strong> — the care details you describe (support needs,
                night expectations, any additional details) are shown to carers browsing JCC so they
                can judge whether the role suits them. Your name, exact address and the care
                recipient's identifying reference are always kept private — never shown publicly.
              </li>
              <li>
                <strong>Care Discussion Rooms, Care Arrangements and shared care information</strong> —
                always private to the specific family and carer involved, never shown to anyone else.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">6. Cookies and sessions</h2>
            <p>
              JCC uses secure, essential cookies only — to keep you signed in and to recognise your
              session. We do not use advertising or tracking cookies.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">7. Data retention</h2>
            <p>
              We keep your information for as long as your account is active, plus a reasonable
              period afterwards where needed for legal, safety or record-keeping reasons. You can ask
              us to delete your account and associated data at any time — see Section 8.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">8. Your rights</h2>
            <p>Under UK data protection law, you have the right to:</p>
            <ul className="mt-2 space-y-1.5 list-disc list-inside marker:text-teal">
              <li>Access the personal information we hold about you.</li>
              <li>Ask us to correct inaccurate information.</li>
              <li>Ask us to delete your information, where applicable.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Request a copy of your information in a portable format.</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, email{" "}
              <a href="mailto:hello@justcarecommunity.com" className="text-teal hover:underline">hello@justcarecommunity.com</a>.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">9. Security</h2>
            <p>
              We use industry-standard security practices — including encrypted connections and
              secure, access-controlled storage — to protect your information. No online service can
              guarantee perfect security, but we take reasonable steps to keep your data safe.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">10. Changes to this policy</h2>
            <p>
              We may update this policy from time to time as JCC evolves. We'll update the "Last
              updated" date above when we do.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">11. Contact us</h2>
            <p>
              Questions about this policy or your information? Email{" "}
              <a href="mailto:hello@justcarecommunity.com" className="text-teal hover:underline">hello@justcarecommunity.com</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
